import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { Grid, Button, CircularProgress } from '@material-ui/core';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import Container from '@material-ui/core/Container';
import moment from 'moment';

import { PaginatedResponse } from '../../../shared/models';
import DateShortcutDropdown, { DateShortcut } from '../../components/DateShortcutDropdown';
import { getPosSalesByPaymentMethodReport } from '../../api';
import { checkStatus, parseJSON, handleError } from '../../../shared/api/core/common';
import { SVG_ICONS } from '../../../shared/icons/SvgIcons';
import { ErrorStatusBar, CustomTableTitle, ExportMenu, ExportStatusBar, } from '../../../shared/components';
import { EMPTY_ROW_MSG, ERROR_500, ERROR_MSG } from '../../../shared/lib/Localization';
import { initQueryParams, replaceHistory, tableOptions, useQuery } from '../../../shared/lib/TableQueryParams';
import download from 'downloadjs';

import GlobalStyles from '../../../../styles/GlobalStyles.web';
import MaterialTable, { MTableToolbar } from 'material-table';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      padding: theme.spacing(3),
      marginBottom: theme.spacing(2),
    },
    formControl: {
      minWidth: 120,
      width: '100%',
      maxWidth: '16rem'
    },
    progress: {
      margin: theme.spacing(2),
    },
    select: {
      color: 'black',
      marginRight: 4,
      '&:before': {
        borderColor: GlobalStyles.primaryColor,
      },
      '&:after': {
        borderColor: GlobalStyles.primaryColor,
      },
      maxWidth: '15rem',
      minWidth: '12rem',
    },
    divider: {
      margin: theme.spacing(0.5, 0),
    },
    filterButtonContainer: {
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(1),
        marginBottom: theme.spacing(2),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
  }),
);

const snakeToTitleCase = (str: string) => {
  if (!str) return '';
  return str
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const PosSalesByPaymentMethodReport = (props: any) => {
  const classes = useStyles({});
  let tableRef = useRef<{ onQueryChange: any; onChangePage: any; }>();
  const [columns, setColumns] = useState<any[]>([
    { title: 'Date Range', field: 'start_date', type: 'date', render: (rowData: any) => {
      if (rowData.start_date) {
        const startDate = new Date(rowData.start_date);
        const endDate = new Date(rowData.end_date);
        return `${moment(startDate).format('YYYY-MM-DD')} - ${moment(endDate).format('YYYY-MM-DD')}`;
      }
      return '';
    } },
    { title: 'POS Station', field: 'pos_name' },
    { title: 'Gross Revenue', field: 'gross_revenue', type: 'currency' },
    { title: 'Net Revenue', field: 'net_revenue', type: 'currency' },
    { title: 'Tax', field: 'tax', type: 'currency' },
  ]);
  const [emptyDataSourceMessage, setEmptyDataSourceMessage] = useState(EMPTY_ROW_MSG);
  const [isErrorStatusVisible, setErrorStatusVisible] = useState(false);
  let locationQuery = useQuery(useLocation);
  const [tableQuery, setTableQuery] = useState(initQueryParams(locationQuery));
  const [isLoading, setLoading] = useState(false);
  const [isExporting, setExporting] = useState(false);
  const [exportBtnAnchorEl, setExportBtnAnchorEl] = React.useState<null | HTMLElement>(null);
  
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [currentShortcut, setCurrentShortcut] = useState<string>('today');

  const formatDateForAPI = (date: Date | null): string => {
    if (!date) return '';
    return moment(date).format('YYYY-MM-DDTHH:mm:ss');
  };

  const allShortcutOptions = [
    { value: DateShortcut.Custom, label: 'Custom Range' },
    { value: DateShortcut.Today, label: 'Today' },
    { value: DateShortcut.Yesterday, label: 'Yesterday' },
    { value: DateShortcut.ThisMonth, label: 'This Month' },
    { value: DateShortcut.LastMonth, label: 'Last Month' },
    { value: DateShortcut.ThisYear, label: 'This Year' },
    { value: DateShortcut.LastYear, label: 'Last Year' },
  ];

  const exportCSV = () => {
    if (props.userData && props.userData.selectedBusiness) {
      setExporting(true);
      const sb = props.userData.selectedBusiness;
      const location_ids = props.userData ? props.userData.selectedLocations.map(l => l.id) : [];
      const location_id_str = location_ids && location_ids.length > 0 ? location_ids.join(",") : [];

      getPosSalesByPaymentMethodReport(
        sb.business_id,
        location_id_str,
        formatDateForAPI(startDate),
        formatDateForAPI(endDate),
        null,
        null,
        null,
        null,
        true
      )
        .catch(handleError)
        .then(checkStatus)
        .then((res) => {
          const contentDisposition = res.headers.get('content-disposition');
          let filename = 'pos_sales_by_payment_method_report.csv';
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
            if (filenameMatch && filenameMatch.length > 1) {
              filename = filenameMatch[1];
            }
          }
          return res.blob().then(blob => ({ blob, filename }));
        })
        .then(({ blob, filename }) => {
          download(blob, filename, 'text/csv');
          setExporting(false);
        })
        .catch((error: any) => {
          setExporting(false);
          if (error) {
            if (error.status && error.status === 500) {
              setEmptyDataSourceMessage(ERROR_MSG);
              setErrorStatusVisible(false);
            } else if (error.status === 403 || error.status === 401) {
              setEmptyDataSourceMessage(ERROR_MSG);
              setErrorStatusVisible(true);
            } else {
              setEmptyDataSourceMessage(ERROR_MSG);
              setErrorStatusVisible(false);
            }
          }
        });
    }
  };

  const handleExportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setExportBtnAnchorEl(event.currentTarget);
  };

  const handleExportMenuClose = (shouldExport: boolean) => {
    if (shouldExport) {
      exportCSV();
    }
    setExportBtnAnchorEl(null);
  };

  const handleGenerateReport = () => {
      if (tableRef.current) {
          tableRef.current.onQueryChange();
      }
  }


  return (
    <Container maxWidth={false}>
      <ErrorStatusBar isVisible={isErrorStatusVisible} />
      <ExportStatusBar isVisible={isExporting} />

      <div className="table-wrapper">
          <MaterialTable
            options={{
              ...tableOptions(tableQuery),
              thirdSortClick: false,
              draggable: false,
              sorting: true, 
              search: false
            }}
            tableRef={tableRef}
            localization={{
              body: {
                emptyDataSourceMessage: emptyDataSourceMessage
              }
            }}
            columns={columns}
            data={query =>
              new Promise((resolve, reject) => {
                if (!startDate || !endDate) {
                    setLoading(false);
                    resolve({
                        data: [],
                        page: 0,
                        totalCount: 0,
                    });
                    return;
                }
                replaceHistory(query, props.history);
                let pageData = {
                  data: [] as any[],
                  totalCount: 0,
                  page: query.page
                };
                let orderBy = '';
                if (query.orderBy) {
                    let field = query.orderBy.field;
                    // Adapt orderBy fields for the new report
                    if (field === 'date') {
                        orderBy = 'date';
                    } else if (field === 'pos_station') {
                        orderBy = 'pos_station';
                    }
                    // Add other fields as needed
                    if (orderBy) {
                        orderBy = `${query.orderDirection && query.orderDirection === 'desc' ? '-' : ''}${orderBy}`;
                    }
                }
                setLoading(true);
                const sb = props.userData.selectedBusiness;
                const location_ids = props.userData ? props.userData.selectedLocations.map(l => l.id) : [];
                const location_id_str = location_ids && location_ids.length > 0 ? location_ids.join(",") : [];

                // Replace with new API call for POS Sales By Payment Method Report
                // getPosSalesByPaymentMethodReport(
                //   sb.business_id,
                //   formatDateForAPI(startDate),
                //   formatDateForAPI(endDate),
                //   query.page + 1,
                //   query.pageSize,
                //   query.search,
                //   orderBy
                // )
                getPosSalesByPaymentMethodReport(
                  sb.business_id,
                  location_id_str,
                  formatDateForAPI(startDate),
                  formatDateForAPI(endDate),
                  query.page + 1,
                  query.pageSize,
                  query.search,
                  orderBy
                )
                  .catch(handleError)
                  .then(checkStatus)
                  .then(parseJSON)
                  .then((data: PaginatedResponse<any>) => {
                    setLoading(false);
                    setErrorStatusVisible(false);
                    setEmptyDataSourceMessage(EMPTY_ROW_MSG);

                    if (data.results && data.results.length > 0) {
                      const allPaymentMethodKeys = new Set<string>();
                      const allAccountTransactionKeys = new Set<string>();

                      data.results.forEach(row => {
                          if (row.payment_methods) {
                              Object.keys(row.payment_methods).forEach(key => allPaymentMethodKeys.add(key));
                          }
                          if (row.account_transactions) {
                              Object.keys(row.account_transactions).forEach(key => allAccountTransactionKeys.add(key));
                          }
                      });

                      const paymentMethodKeys = Array.from(allPaymentMethodKeys);
                      const accountTransactionKeys = Array.from(allAccountTransactionKeys);

                      const paymentMethodColumns = paymentMethodKeys.map(key => ({
                          title: snakeToTitleCase(key),
                          field: `payment_methods.${key}`,
                          type: 'currency'
                      }));

                      const accountTransactionColumns = accountTransactionKeys.map(key => ({
                          title: snakeToTitleCase(key),
                          field: `account_transactions.${key}`,
                          type: 'currency'
                      }));

                      const baseColumns = [
                        { title: 'Date Range', field: 'start_date', type: 'date', render: (rowData: any) => {
                          if (rowData.start_date) {
                            const startDate = new Date(rowData.start_date);
                            const endDate = new Date(rowData.end_date);
                            return `${moment(startDate).format('YYYY-MM-DD')} - ${moment(endDate).format('YYYY-MM-DD')}`;
                          }
                          return '';
                        } },
                        { title: 'POS Station', field: 'pos_name' },
                      ];

                      const revenueColumns = [
                        // { title: 'Modified Gross Revenue', field: 'modified_gross_revenue', type: 'currency' },
                        { title: 'Gross Revenue', field: 'modified_gross_revenue', type: 'currency' },
                        { title: 'Net Revenue', field: 'modified_net_revenue', type: 'currency' },
                        { title: 'Tax', field: 'tax', type: 'currency' },
                      ];

                      const newColumns = [
                          ...baseColumns,
                          ...paymentMethodColumns,
                          ...accountTransactionColumns,
                          ...revenueColumns,
                      ];
                      
                      if (JSON.stringify(columns) !== JSON.stringify(newColumns)) {
                          setColumns(newColumns);
                      }
                    }

                    pageData = {
                      data: data.results,
                      totalCount: data.count,
                      page: query.page
                    };
                    setTableQuery({
                      ...tableQuery,
                      orderBy,
                      totalCount: data.count,
                      page: query.page,
                      pageSize: query.pageSize
                    });
                    resolve(pageData);
                  })
                  .catch((error: any) => {
                    if (error) {
                      if (error.status && error.status === 500) {
                        setEmptyDataSourceMessage(ERROR_500);
                        setErrorStatusVisible(false);
                      } else if ((error.status === 403 || error.status === 401)
                      ) {
                        setEmptyDataSourceMessage(ERROR_MSG);
                        setErrorStatusVisible(true);
                      } else {
                        setEmptyDataSourceMessage(ERROR_MSG);
                        setErrorStatusVisible(false);
                      }
                    }
                    setTableQuery({
                      ...tableQuery,
                      totalCount: 0,
                      page: query.page,
                      pageSize: query.pageSize
                    });
                    setLoading(false);

                    resolve(pageData);
                  });
              })
            }
            title={
              <CustomTableTitle
                history={props.history}
                title={'POS Sales By Payment Method Report'}
                icon={SVG_ICONS.product} />
            }
            components={{
                Toolbar: (toolbarProps) => (
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <>
                            <MTableToolbar {...toolbarProps} />
                            <Grid container spacing={2} alignItems="center" className={classes.filterButtonContainer}>
                                <Grid item>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item>
                                            <DateShortcutDropdown
                                                key={`current-${currentShortcut}`}
                                                label="Date Shortcuts"
                                                setStartDate={(newDate) => setStartDate(currentDate => {
                                                    if (newDate === null && currentDate === null) return currentDate;
                                                    if (newDate === null || currentDate === null) return newDate;
                                                    if (newDate.getTime() !== currentDate.getTime()) return newDate;
                                                    return currentDate;
                                                })}
                                                setEndDate={(newDate) => setEndDate(currentDate => {
                                                    if (newDate === null && currentDate === null) return currentDate;
                                                    if (newDate === null || currentDate === null) return newDate;
                                                    if (newDate.getTime() !== currentDate.getTime()) return newDate;
                                                    return currentDate;
                                                })}
                                                value={currentShortcut}
                                                onChange={(newShortcut) => {
                                                    if (newShortcut !== currentShortcut) {
                                                        setCurrentShortcut(newShortcut);
                                                    }
                                                }}
                                                className={classes.formControl}
                                                shortcutOptions={allShortcutOptions}
                                            />
                                        </Grid>
                                        {currentShortcut === DateShortcut.Custom && (
                                            <Grid item>
                                                <Grid container spacing={1}>
                                                    <Grid item>
                                                        <KeyboardDatePicker
                                                            fullWidth
                                                            label="Start Date"
                                                            value={startDate}
                                                            onChange={(date) => setStartDate(date ? new Date(date as any) : null)}
                                                            format="yyyy-MM-dd"
                                                            className={classes.formControl}
                                                            autoOk
                                                            openTo="month"
                                                            views={['year', 'month', 'date']}
                                                            maxDate={new Date()}
                                                            maxDateMessage="Date cannot be in the future"
                                                        />
                                                    </Grid>
                                                    <Grid item>
                                                        <KeyboardDatePicker
                                                            fullWidth
                                                            label="End Date"
                                                            value={endDate}
                                                            onChange={(date) => setEndDate(date ? new Date(date as any) : null)}
                                                            format="yyyy-MM-dd"
                                                            className={classes.formControl}
                                                            autoOk
                                                            openTo="month"
                                                            views={['year', 'month', 'date']}
                                                            maxDate={new Date()}
                                                            maxDateMessage="Date cannot be in the future"
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Grid>
                                <Grid item>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleGenerateReport}
                                        disabled={isLoading}
                                        style={{ marginRight: '10px' }}
                                    >
                                        {isLoading ? <CircularProgress size={24} /> : 'Generate Report'}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={exportCSV}
                                        disabled={isExporting}
                                    >
                                        {isExporting ? <CircularProgress size={24} /> : 'Export CSV'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </>
                    </MuiPickersUtilsProvider>
                ),
            }}
          />
        </div>
        <ExportMenu anchorEl={exportBtnAnchorEl} handleClose={handleExportMenuClose} />
    </Container>
  );
};

const mapStateToProps = (state: any) => {
  return {
    userData: state.userLocalData,
  };
};

export default connect(mapStateToProps, null)(PosSalesByPaymentMethodReport);
